/**
 * @file FormCTA - Integrated with Existing Leads DB Schema
 * @description Lead generation form that integrates with the existing
 * leads table schema and server actions.
 * 
 * @dependencies
 * - react-hook-form: Form state management
 * - zod: Schema validation
 * - createLead: Server action from lib/actions/leads.ts
 */

'use client';

import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { createLead } from '@/lib/actions/leads';
import { 
  CheckCircle2, Loader2, ArrowRight, 
  Eye, EyeOff, Info, AlertTriangle,
  User, Mail, Phone, Building, MessageSquare, Lock
} from 'lucide-react';
import posthog from 'posthog-js';

// Zod Schema - Matches server-side validation exactly
const formSchema = z.object({
  name: z.string().min(2, "Nome troppo corto"),
  surname: z.string().min(2, "Cognome troppo corto"),
  companyEmail: z.string().email("Email non valida"),
  phoneNumber: z.string().min(5, "Numero di telefono non valido").optional().or(z.literal('')),
  note: z.string().max(500, "Note troppo lunghe").optional().or(z.literal('')),
  type: z.string(),
  source: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof formSchema>;

// CTA Type specific labels and auto-notes
const CTA_CONFIG: Record<string, {
  submit: string;
  successNew: string;
  successDuplicate: string;
  placeholder: {
    note: string;
    brand: string;
    phone: string;
  };
}> = {
  trial: {
    submit: "Inizia la Prova Gratuita",
    successNew: "Richiesta inviata! Controlla la tua email.",
    successDuplicate: "Email già registrata. Controlla la tua email per accedere.",
    placeholder: {
      note: "Parlaci dei tuoi brand principali (opzionale)",
      brand: "I tuoi brand principali",
      phone: "Il tuo numero di telefono",
    },
  },
  audit: {
    submit: "Richiedi Audit Gratuito",
    successNew: "Audit richiesto! Ti contattiamo entro 24 ore.",
    successDuplicate: "Sei già nel nostro sistema. Controlla la tua email per i dettagli.",
    placeholder: {
      note: "Descrivi brevemente la tua situazione attuale",
      brand: "Brand per cui lavori",
      phone: "Il tuo numero di telefono",
    },
  },
  demo: {
    submit: "Prenota la Demo",
    successNew: "Demo prenotata! Controlla la tua email per la conferma.",
    successDuplicate: "Sei già registrato. Accedi per confermare il tuo appuntamento.",
    placeholder: {
      note: "Cosa vorresti vedere nella demo?",
      brand: "Brand per cui lavori",
      phone: "Il tuo numero di telefono",
    },
  },
  contact: {
    submit: "Invia Messaggio",
    successNew: "Messaggio inviato! Ti rispondiamo al più presto.",
    successDuplicate: "Sei già nel nostro database. Ti rispondiamo al più presto.",
    placeholder: {
      note: "Come possiamo aiutarti?",
      brand: "La tua agenzia (opzionale)",
      phone: "Il tuo numero di telefono",
    },
  },
};

interface FormCTAProps {
  defaultType: string;
  autoNote: string;
  source?: string;
  brand?: string;
}

export function FormCTA({ defaultType, autoNote, source, brand }: FormCTAProps) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [showNote, setShowNote] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  
  const config = CTA_CONFIG[defaultType] || CTA_CONFIG.trial;
  
  // Build enhanced note that includes CTA type context
  const buildEnhancedNote = useCallback((baseNote: string, ctaType: string, brandText?: string): string => {
    const parts: string[] = [];
    
    // Add CTA type indicator
    const typeLabels: Record<string, string> = {
      trial: '[PROVA] ',
      audit: '[AUDIT] ',
      demo: '[DEMO] ',
      contact: '[CONTATTO] ',
    };
    
    if (typeLabels[ctaType]) {
      parts.push(typeLabels[ctaType]);
    }
    
    // Add the user's note
    if (baseNote && baseNote !== autoNote) {
      parts.push(baseNote);
    }
    
    // Add brand info if provided
    if (brandText && brandText.trim()) {
      parts.push(`Brand: ${brandText.trim()}`);
    }
    
    return parts.join(' | ');
  }, [autoNote]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      surname: '',
      companyEmail: '',
      phoneNumber: '',
      note: autoNote,
      type: defaultType,
      source: source || '',
    },
  });

  async function onSubmit(data: FormData) {
    setErrorMessage(null);
    setIsDuplicate(false);
    
    try {
      // Build the note with CTA context
      const enhancedNote = buildEnhancedNote(data.note || '', data.type, brand);
      
      const result = await createLead({
        name: data.name,
        surname: data.surname,
        companyEmail: data.companyEmail,
        phoneNumber: data.phoneNumber || undefined,
        note: enhancedNote || undefined,
      });

      if (result.success) {
        posthog.capture('lead_created', { 
            location: 'form_cta',
            is_duplicate: result.isDuplicate,
            lead_type: data.type
        });

        // Check if this was a duplicate registration
        if (result.isDuplicate) {
          setIsDuplicate(true);
          setSubmittedEmail(data.companyEmail);
          // Keep the form data for user reference
        } else {
          setIsDuplicate(false);
          setSubmittedEmail(data.companyEmail);
          reset(); // Clear form only for new leads
        }
        
        setIsSuccess(true);
      } else {
        setErrorMessage(result.error || "Si è verificato un errore imprevisto.");
      }
    } catch (error) {
      setErrorMessage("Si è verificato un errore di connessione. Riprova.");
    }
  }

  const handleReset = () => {
    setIsSuccess(false);
    setIsDuplicate(false);
    setSubmittedEmail(null);
    setErrorMessage(null);
    reset({
      name: '',
      surname: '',
      companyEmail: '',
      phoneNumber: '',
      note: autoNote,
      type: defaultType,
      source: source || '',
    });
  };

  // Success State
  if (isSuccess) {
    return (
      <div className="text-center py-6" role="status" aria-live="polite">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${isDuplicate ? 'bg-blue-50' : 'bg-emerald-100'}`}>
          {isDuplicate ? (
            <Info className="w-8 h-8 text-blue-600" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="w-8 h-8 text-emerald-600" aria-hidden="true" />
          )}
        </div>
        
        <h3 className="text-xl font-bold text-slate-900 mb-3">
          {isDuplicate ? config.successDuplicate : config.successNew}
        </h3>
        
        <p className="text-slate-600 mb-6 leading-relaxed">
          {isDuplicate 
            ? `Sembra che l'email <strong>${submittedEmail}</strong> sia già presente nei nostri sistemi. `
            : `Grazie per il tuo interesse! `
          }
          {defaultType === 'trial' && !isDuplicate && (
            "Abbiamo inviato una email di conferma con i dettagli per accedere alla tua prova gratuita."
          )}
          {defaultType === 'audit' && (
            <>
              {isDuplicate 
                ? "Abbiamo riattivato la tua richiesta. Controlla la tua casella di posta."
                : "Ti contatteremo entro 24 ore per analizzare i tuoi dati."
              }
            </>
          )}
          {defaultType === 'demo' && (
            <>
              {isDuplicate 
                ? "Accedi per confermare il tuo appuntamento."
                : "Controlla la tua email per confermare il tuo slot."
              }
            </>
          )}
          {defaultType === 'contact' && (
            "Il nostro team ti risponderà al più presto."
          )}
        </p>

        {/* What happens next - only for new leads */}
        {!isDuplicate && (
          <div className="bg-slate-50 rounded-xl p-5 text-left max-w-md mx-auto mb-6">
            <h4 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wide text-slate-500">
              Cosa succede ora?
            </h4>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xs">1</span>
                <span>Ricevi una email di conferma</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xs">2</span>
                <span>
                  {defaultType === 'audit' 
                    ? 'Il nostro team analizza i tuoi dati' 
                    : 'Configuri il tuo account in 5 minuti'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xs">3</span>
                <span>Inizi a usare KennyDB</span>
              </li>
            </ul>
          </div>
        )}
        
        {/* Already registered actions */}
        {isDuplicate && (
          <div className="bg-blue-50 rounded-xl p-5 text-left max-w-md mx-auto mb-6">
            <h4 className="font-semibold text-blue-900 mb-3 text-sm uppercase tracking-wide">
              I tuoi prossimi passi
            </h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600" aria-hidden="true" />
                <span>Controlla la tua email per il link di accesso</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600" aria-hidden="true" />
                <span>Recupera la password se necessario</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600" aria-hidden="true" />
                <span>Accedi e completa il tuo profilo</span>
              </li>
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            onClick={handleReset}
            variant="outline"
            className="border-slate-300 hover:bg-slate-50"
          >
            Torna alla Home
          </Button>
          {!isDuplicate && defaultType === 'trial' && (
            <Button 
              onClick={handleReset}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Un'altra richiesta
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Form State
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      {/* Hidden fields */}
      <input type="hidden" {...register('type')} />
      <input type="hidden" {...register('source')} />
      
      {/* Rate Limit Error */}
      {errorMessage?.includes('limite') && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-start gap-2" role="alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* General Error */}
      {errorMessage && !errorMessage.includes('limite') && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" role="alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Name Row */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-sm font-medium text-slate-700">
            Nome <span className="text-red-500" aria-hidden="true">*</span>
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
            <Input
              id="name"
              type="text"
              placeholder="Mario"
              {...register('name')}
              className={`pl-10 ${errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
              aria-invalid={errors.name ? 'true' : 'false'}
            />
          </div>
          {errors.name && (
            <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" />
              {errors.name.message}
            </p>
          )}
        </div>
        
        <div className="space-y-1.5">
          <Label htmlFor="surname" className="text-sm font-medium text-slate-700">
            Cognome <span className="text-red-500" aria-hidden="true">*</span>
          </Label>
          <Input
            id="surname"
            type="text"
            placeholder="Rossi"
            {...register('surname')}
            className={errors.surname ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}
            aria-invalid={errors.surname ? 'true' : 'false'}
          />
          {errors.surname && (
            <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" />
              {errors.surname.message}
            </p>
          )}
        </div>
      </div>
      
      {/* Email Field */}
      <div className="space-y-1.5">
        <Label htmlFor="companyEmail" className="text-sm font-medium text-slate-700">
          Email Aziendale <span className="text-red-500" aria-hidden="true">*</span>
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
          <Input
            id="companyEmail"
            type="email"
            placeholder="mario.rossi@email.com"
            {...register('companyEmail')}
            className={`pl-10 ${errors.companyEmail ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
            aria-invalid={errors.companyEmail ? 'true' : 'false'}
            autoComplete="email"
          />
        </div>
        {errors.companyEmail && (
          <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            {errors.companyEmail.message}
          </p>
        )}
      </div>
      
      {/* Phone Field */}
      <div className="space-y-1.5">
        <Label htmlFor="phoneNumber" className="text-sm font-medium text-slate-700">
          Telefono <span className="text-red-500" aria-hidden="true">*</span>
        </Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
          <Input
            id="phoneNumber"
            type="tel"
            placeholder="+39 333 1234567"
            {...register('phoneNumber')}
            className={`pl-10 ${errors.phoneNumber ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
            aria-invalid={errors.phoneNumber ? 'true' : 'false'}
            autoComplete="tel"
          />
        </div>
        {errors.phoneNumber && (
          <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            {errors.phoneNumber.message}
          </p>
        )}
      </div>
      
      {/* Brand Field (shown as optional note context) */}
      {brand && (
        <div className="space-y-1.5">
          <Label htmlFor="brand-display" className="text-sm font-medium text-slate-700">
            Brand selezionato
          </Label>
          <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 text-sm">
            {brand}
          </div>
        </div>
      )}
      
      {/* Note Toggle & Field */}
      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => setShowNote(!showNote)}
          className="text-sm text-indigo-600 font-medium hover:text-indigo-700 flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 rounded px-1 py-0.5"
          aria-expanded={showNote}
        >
          {showNote ? (
            <>
              <EyeOff className="w-4 h-4" aria-hidden="true" />
              Nascondi nota
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" aria-hidden="true" />
              Aggiungi una nota
            </>
          )}
        </button>
        
        {showNote && (
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" aria-hidden="true" />
            <Textarea
              id="note"
              placeholder={config.placeholder.note}
              {...register('note')}
              className="pl-10 min-h-[80px] resize-none text-sm"
              aria-describedby="note-hint"
            />
            <p id="note-hint" className="text-xs text-slate-500 mt-1">
              Informazioni aggiuntive sui tuoi brand o esigenze
            </p>
          </div>
        )}
      </div>
      
      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#0d1b2a] hover:bg-[#1b2838] text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" />
            Elaborazione...
          </>
        ) : (
          <>
            {config.submit}
            <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
          </>
        )}
      </Button>
      
      {/* Trust Microcopy */}
      <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-2">
        <Lock className="w-3.5 h-3.5" aria-hidden="true" />
        I tuoi dati sono protetti. Nessuna carta richiesta per la prova.
      </p>
    </form>
  );
}